import { Typography, MenuItem, Select, FormControl, Tooltip, Button } from '@mui/material';
import { useDispatch } from 'react-redux';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { updateMeasureMappingColumn } from '../../../redux/ChartPoperties/ChartControlsActions';

interface MeasureField {
  fieldname: string;
  displayname: string;
  dataType: string;
  tableId: string;
  hasOverridedFlow: boolean;
  uId: string;
  agg: string;
  SavedCalculationUUID: string | null;
  isParameterized: boolean;
  isTextRenamed?: boolean;
}

interface MeasureMappingProps {
  measureFields: MeasureField[];
  measureDropFields: (string | number | null)[] | null;
  selectedMapping: Record<string, string>[];
  setSelectedMapping: (newMapping: Record<string, string>[]) => void;
  propKey: string;
  currentOperator: 'user' | 'group';
  savedMappings: Record<string, string>[];
}

const MeasureMapping = ({
  measureFields,
  measureDropFields,
  selectedMapping,
  setSelectedMapping,
  propKey,
  currentOperator,
  savedMappings,
}: MeasureMappingProps) => {
  const dispatch = useDispatch();

  const handleDropdownChange = (index: number, value: string) => {
    const sourceMeasure = measureFields[index];
    const sourceMeasureUId = sourceMeasure.uId;

    const idx = selectedMapping.findIndex((obj) => obj[sourceMeasureUId] !== undefined);

    const updated = [...selectedMapping];

    if (idx !== -1) {
      updated[idx] = { [sourceMeasureUId]: value };
    } else {
      updated.push({ [sourceMeasureUId]: value });
    }

    setSelectedMapping(updated);
  };

  const handleProceedClick = () => {
    dispatch(updateMeasureMappingColumn(propKey, currentOperator, selectedMapping));
  };

  const handleCancelClick = () => {
    setSelectedMapping(savedMappings);
  };

  const getSavedMappingValue = (uId: string): string => {
    const found = selectedMapping?.find((obj) => Object.keys(obj)[0] === uId);
    return found?.[uId] ?? '';
  };

  return (
    <>
      <div
        className="optionDescription"
        style={{
          borderTop: '2px solid rgb(224, 224, 224)',
          paddingTop: '10px',
          fontWeight: 600,
          fontSize: '12px',
          color: '#515050',
          textAlign: 'left',
          paddingLeft: '4px',
          marginBottom: '6px',
        }}
      >
        Measure Mapping
        <Tooltip
          title="Measure mapping allows connecting the chart's measure to user's allowed measure."
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
          placement="top"
          arrow
        >
          <InfoOutlineIcon
            fontSize="small"
            style={{
              marginLeft: '4px',
              verticalAlign: 'middle',
              color: '#888',
            }}
          />
        </Tooltip>
      </div>
      {measureFields.map((measure, idx) => {
        const uId = measure.uId;
        const savedValue = getSavedMappingValue(uId);
        const selectedValue = savedValue;
        return (
          <div key={measure.uId}>
            <Typography
              sx={{
                marginBottom: '1px',
                fontSize: '13px',
                display: 'flex',
                gap: '0.4rem',
                alignItems: 'center',
                marginInline: '0.3rem',
              }}
            >
              {measure.displayname}
            </Typography>

            <FormControl
              fullWidth
              size="small"
              style={{
                fontSize: '12px',
                borderRadius: '4px',
                textAlign: 'left',
              }}
            >
              <Select
                label=""
                value={selectedValue}
                onChange={(e) => handleDropdownChange(idx, e.target.value)}
                variant="outlined"
                sx={{
                  fontSize: '12px',
                  width: '99%',
                  margin: '0 auto 0.5rem auto',
                  backgroundColor: 'white',
                  height: '1.5rem',
                  color: '#404040',
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2bb9bb',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root.Mui-selected': {
                        backgroundColor: 'rgba(43, 185, 187, 0.1) !important',
                      },
                      '& .MuiMenuItem-root.Mui-selected:hover': {
                        backgroundColor: 'rgba(43, 185, 187, 0.2) !important',
                      },
                    },
                  },
                }}
              >
                {(measureDropFields ?? [])
                  .filter((field): field is string | number => field !== null)
                  .map((field) => (
                    <MenuItem key={field} value={field}>
                      {field}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </div>
        );
      })}
      <div
        className="optionDescription"
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          gap: '15px',
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          onClick={() => handleCancelClick()}
          sx={{
            fontSize: '10px',
            boxShadow: 'none',
            border: '2px solid #b6b6b6;',
            borderRadius: '2px',
            textTransform: 'initial',
            '&:hover': {
              color: 'white',
              backgroundColor: '#b6b6b6',
              boxShadow: '0px 0px 2px 1px rgb(199, 199, 199)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          disabled={measureFields.length !== selectedMapping.length}
          variant="outlined"
          color="inherit"
          size="small"
          onClick={() => handleProceedClick()}
          sx={{
            cursor: measureFields.length === selectedMapping.length ? 'pointer' : 'not-allowed',
            fontSize: '10px',
            boxShadow: 'none',
            border: '2px solid #2bb9bb',
            borderRadius: '1px',
            textTransform: 'initial',
            '&:hover': {
              color: 'white',
              backgroundColor: '#2bb9bb',
              boxShadow: '0px 0px 2px 1px #2bb9bb',
            },
          }}
        >
          Ok
        </Button>
      </div>
    </>
  );
};

export default MeasureMapping;
